import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Check, HeartStraight } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { ComponentProps, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import {
    getFollowedSellerLoading,
    isSellerFollowed,
    submitFollowSeller,
    submitUnfollowSeller,
} from '@/redux/modules/followSeller';
import { getItemSummaryCatalogId } from '@/redux/modules/item/summary/itemSummary.selectors';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';
import {
    useTrackAuctioneerFollowedAnalytics,
    useTrackAuctioneerUnfollowedAnalytics,
} from '@/utils/amplitude/analytics';
import noop from '@/utils/noop';
import { getItemIdFromPath } from '@/utils/urls';

type Props = {
    className?: string;
    followText?: string;
    followingText?: string;
    longText?: boolean;
    sellerId: number;
    small?: boolean;
    variant?: ComponentProps<typeof Button>['variant'];
};

const FollowSellerButton = ({
    className,
    followingText = 'bidding_console.console.currentLiveItem.auctioneerInfo.buttons.following',
    followText = 'bidding_console.console.currentLiveItem.auctioneerInfo.buttons.follow',
    longText,
    sellerId,
    small,
    variant,
}: Props) => {
    const followed = useAppSelector((state) => isSellerFollowed(state, sellerId));
    const followedSellerLoading = useAppSelector(getFollowedSellerLoading);
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const itemId = getItemIdFromPath(pathname);
    const itemCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const itemSellerId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));

    const trackAuctioneerFollowedAnalytics = useTrackAuctioneerFollowedAnalytics();
    const trackAuctioneerUnfollowedAnalytics = useTrackAuctioneerUnfollowedAnalytics();

    const { follow, following } = useMemo(
        () => ({
            follow: longText
                ? 'bidding_console.console.currentLiveItem.auctioneerInfo.buttons.followAuctioneer'
                : followText,
            following: longText
                ? 'bidding_console.console.currentLiveItem.auctioneerInfo.buttons.followingAuctioneer'
                : followingText,
        }),
        [followText, followingText, longText]
    );

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId: itemCatalogId || 0,
            eventContext: EventContext.followAuctioneer,
            id: itemSellerId || sellerId,
            itemId: itemId || 0,
            sellerId: sellerId || itemSellerId || 0,
            type: CompleteAccountModalContext.FollowAuctioneer,
        }),
        [itemId, itemCatalogId, itemSellerId, sellerId]
    );

    const toggleFollowSeller = useCallback(() => {
        if (followed) {
            dispatch(submitUnfollowSeller(sellerId));
            trackAuctioneerUnfollowedAnalytics();
        } else {
            dispatch(submitFollowSeller(sellerId));
            trackAuctioneerFollowedAnalytics();
        }
    }, [dispatch, followed, sellerId, trackAuctioneerFollowedAnalytics, trackAuctioneerUnfollowedAnalytics]);

    return (
        <ShouldLogInContainer
            modalContext={modalContext}
            requireLogin
            runFunction={toggleFollowSeller}
        >
            <Button
                className={className}
                data-testid="followSellerButton"
                iconLeft={followed ? Check : HeartStraight}
                iconStyle={followed ? 'bold' : 'regular'}
                loading={followedSellerLoading}
                onClick={noop}
                size={small ? 'sm' : 'md'}
                variant={variant ?? (followed ? 'outlined' : 'filled')}
            >
                {formatMessage({ id: followed ? following : follow })}
            </Button>
        </ShouldLogInContainer>
    );
};

export default FollowSellerButton;
